import type { SyncConnection } from './engine';

export type ConnectionBannerTone = 'offline' | 'warning';

export type ConnectionBannerState = {
  tone: ConnectionBannerTone;
  title: string;
  detail: string;
};

function pendingSuffix(pendingCount: number): string {
  if (pendingCount <= 0) {
    return '';
  }
  const noun = pendingCount === 1 ? 'change' : 'changes';
  return ` ${pendingCount} ${noun} waiting to sync.`;
}

/**
 * Banner copy for the current connection, or null when nothing should show.
 *
 * `offline` takes priority: changes are held locally and the user needs to know
 * they are seeing local-only data. When online (or reconnecting) but some server
 * deltas failed to apply, a `warning` banner surfaces the divergence — this used
 * to be silent, leaving two devices showing different content. `connecting` with
 * a clean queue stays silent so the UI never flashes a warning on a healthy load.
 */
export function connectionBannerState(connection: SyncConnection): ConnectionBannerState | null {
  if (connection.status === 'offline') {
    return {
      tone: 'offline',
      title: "You're offline",
      detail:
        'Changes are saved on this device and will sync automatically when the connection returns.' +
        pendingSuffix(connection.pendingCount),
    };
  }
  if (connection.quarantinedCount > 0) {
    const noun = connection.quarantinedCount === 1 ? 'item' : 'items';
    return {
      tone: 'warning',
      title: "Some data couldn't sync",
      detail:
        `${connection.quarantinedCount} ${noun} failed to update on this device. ` +
        'Open Settings › Sync & storage to retry or run a full resync.',
    };
  }
  return null;
}
